<template>
  <div class="col-12 connections py-4 mb-4">
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">История</h5>
      </div>
      <div class="card-body">
        <p>
          В разделе содержатся существенные факты деятельности организации в прошлом. Наибольший интерес представляет
          информация о предыдущих руководителях и адресах, изменении структуры учредителей и размера их долей.
        </p>

        <div class="history-filters row gy-3 mb-4">
          <div class="col-md-4">
            <label class="history-label" for="history-source">Раздел</label>
            <select id="history-source" v-model="selectedSection" class="form-select">
              <option value="all">Все</option>
              <option value="timeline">История изменений</option>
              <option value="fedresurs">Федресурс</option>
              <option value="bankruptcy">Банкротство</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="history-label" for="history-sort">Сортировка</label>
            <select id="history-sort" v-model="sortOrder" class="form-select">
              <option value="desc">Сначала новые</option>
              <option value="asc">Сначала старые</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="history-label" for="history-search">Фильтр</label>
            <input
              id="history-search"
              v-model.trim="searchQuery"
              type="text"
              class="form-control"
              placeholder="Тип сообщения или событие"
            />
          </div>
        </div>

        <section v-if="showTimeline" class="mb-5">
          <h6 class="history-section-title">История изменений</h6>
          <div v-if="filteredTimelineEvents.length" class="connections-list-container">
            <div v-for="event in filteredTimelineEvents" :key="event.id" class="connections-list">
              <div class="connections-list__header">
                <p>{{ formatDate(event.event_date) }}</p>
              </div>
              <div class="connections-list__body col-12">
                <p class="value mb-0">{{ event.event_name }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-center mb-0">Нет данных по истории изменений</p>
        </section>

        <section v-if="showFedresurs" class="mb-5">
          <h6 class="history-section-title">Сообщения Федресурса</h6>
          <div v-if="filteredFedresursMessages.length" class="connections-list-container">
            <div v-for="message in filteredFedresursMessages" :key="message.id" class="connections-list">
              <div class="connections-list__header">
                <p>{{ message.message_type_name || message.message_type || 'Сообщение' }}</p>
              </div>
              <div class="connections-list__body col-12">
                <p class="value"><span class="label">Дата</span>{{ formatDate(message.publish_date) }}</p>
                <p class="value"><span class="label">Публикатор</span>{{ message.publisher || 'Нет данных' }}</p>
                <p class="value">
                  <span class="label">Участники</span>{{ formatParticipants(message.participants) }}
                </p>
                <a v-if="message.url" :href="message.url" target="_blank" rel="noopener noreferrer">Открыть сообщение</a>
              </div>
            </div>
          </div>
          <p v-else class="text-center mb-0">Нет сообщений Федресурса</p>
        </section>

        <section v-if="showBankruptcy">
          <h6 class="history-section-title">Сообщения о банкротстве</h6>
          <div v-if="filteredBankruptcyMessages.length" class="connections-list-container">
            <div v-for="message in filteredBankruptcyMessages" :key="message.id" class="connections-list">
              <div class="connections-list__header">
                <p>{{ message.message_type_name || message.message_type || 'Сообщение' }}</p>
              </div>
              <div class="connections-list__body col-12">
                <p class="value"><span class="label">Дата</span>{{ formatDate(message.publish_date) }}</p>
                <p class="value"><span class="label">Публикатор</span>{{ message.publisher || 'Нет данных' }}</p>
                <p class="value">
                  <span class="label">Участники</span>{{ formatParticipants(message.participants) }}
                </p>
                <a v-if="message.url" :href="message.url" target="_blank" rel="noopener noreferrer">Открыть сообщение</a>
              </div>
            </div>
          </div>
          <p v-else class="text-center mb-0">Нет сообщений о банкротстве</p>
        </section>
      </div>
    </div>
  </div>
</template>
<script>
  import { computed, defineComponent, ref } from 'vue';
  import moment from 'moment';

  export default defineComponent({
    name: 'History',
    props: {
      timelineEvents: {
        type: Array,
        default: () => [],
      },
      fedresursMessages: {
        type: Array,
        default: () => [],
      },
      bankruptcyMessages: {
        type: Array,
        default: () => [],
      },
    },
    setup(props) {
      const selectedSection = ref('all');
      const sortOrder = ref('desc');
      const searchQuery = ref('');

      const formatDate = (date) => {
        return date ? moment(date).format('DD.MM.YYYY') : 'Нет данных';
      };

      const formatParticipants = (participants) => {
        return Array.isArray(participants) && participants.length ? participants.join(', ') : 'Нет данных';
      };

      const normalizeDateValue = (date) => {
        return date ? new Date(date).getTime() : 0;
      };

      const applyMessageFilters = (items) => {
        const query = searchQuery.value.toLowerCase();
        const sorted = [...items].sort((a, b) => {
          const diff = normalizeDateValue(a.publish_date) - normalizeDateValue(b.publish_date);
          return sortOrder.value === 'asc' ? diff : -diff;
        });

        if (!query) {
          return sorted;
        }

        return sorted.filter((item) => {
          const haystack = [item.message_type_name, item.message_type, item.publisher, formatParticipants(item.participants)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(query);
        });
      };

      const filteredTimelineEvents = computed(() => {
        const sorted = [...props.timelineEvents].sort((a, b) => {
          const diff = normalizeDateValue(a.event_date) - normalizeDateValue(b.event_date);
          return sortOrder.value === 'asc' ? diff : -diff;
        });

        if (!searchQuery.value) {
          return sorted;
        }

        return sorted.filter((event) => (event.event_name || '').toLowerCase().includes(searchQuery.value.toLowerCase()));
      });

      const filteredFedresursMessages = computed(() => applyMessageFilters(props.fedresursMessages));
      const filteredBankruptcyMessages = computed(() => applyMessageFilters(props.bankruptcyMessages));

      const showTimeline = computed(() => selectedSection.value === 'all' || selectedSection.value === 'timeline');
      const showFedresurs = computed(() => selectedSection.value === 'all' || selectedSection.value === 'fedresurs');
      const showBankruptcy = computed(() => selectedSection.value === 'all' || selectedSection.value === 'bankruptcy');

      return {
        selectedSection,
        sortOrder,
        searchQuery,
        filteredTimelineEvents,
        filteredFedresursMessages,
        filteredBankruptcyMessages,
        showTimeline,
        showFedresurs,
        showBankruptcy,
        formatDate,
        formatParticipants,
      };
    },
  });
</script>
<style scoped lang="scss">
  .history-filters {
    padding: 1rem 0 0.5rem;
  }

  .history-label {
    display: block;
    margin-bottom: 0.5rem;
    color: #6d7785;
    font-size: 14px;
  }

  .history-section-title {
    margin-bottom: 1rem;
    color: #3e3e3e;
    font-weight: 700;
  }
</style>
